/**
 * **The Forma tab's entry point** — canon `docs/design/analiza-wideo-2026-09-23/`, FINAL v4 and v6.
 *
 * One build answers the stage card, the change table, the session table and the dock, because
 * they are one statement about one movement: a sentence naming a different pair from the one the
 * bars were drawn for would be two answers to one question — the rule `model/twoDates` states for
 * Pomiary, and whose selection, trend and forecast rules this module reuses over sessions.
 *
 * The dock is NOT tied to the selection: it shows the movement's newest recording, because it
 * answers "what does the lift look like now", which A and B do not change. The one way to put an
 * older recording there is a double click on its point, and the dock then says so.
 *
 * What the movement is read on (the switch, the rows, the figure columns, Spadek) follows the
 * fields it measures, through `entities/lift-history`'s `movementLines`: a run is read by distance,
 * time and pace, a plank by its longest set and total time, a lift by e1RM.
 */

import {
  byNewestClip,
  clipPersonView,
  commentWhen,
  formatClock,
  newestClip,
  type ClipEntry,
  type ClipPersonView,
} from '~/entities/exercise-clip';
import {
  anchorWords,
  lineReadings,
  movementLines,
  sessionDrop,
  sessionFigure,
  setInFields,
  sizedLineWords,
  type LiftFields,
  type LiftLine,
  type LiftSession,
  type LineWords,
} from '~/entities/lift-history';
import { dateTimeFormat, numberFormat } from '~/shared/lib/intlCache';
import type { Translate } from '~/shared/lib/localeTranslate';
import { divergingBar } from '~/shared/lib/divergingBar';
import { moveOnTimes } from '~/shared/lib/markerSelection';
import { signedText } from '~/shared/lib/signedText';

import type { MeasurementPoint } from './measurements';
import {
  chartAids,
  markerAt,
  sentenceOf,
  snapSelection,
  type MarkerKey,
  type Reading,
  type SelectionMove,
  type TwoDatesMarker,
  type TwoDatesProjection,
  type TwoDatesSelection,
  type TwoDatesSentence,
} from './twoDates';

/** A full bar on the change table is a 20% change (canon). */
const FORMA_SCALE_PERCENT = 20;
/**
 * A full bar reaches this share of the track each side. Shorter than Pomiary's 40: Forma's centre
 * column is narrower, and the percentage printed past the bar must not run into the B figure.
 */
const FORMA_BAR_REACH = 20;

export interface FormaDeps {
  readonly t: Translate;
  readonly locale: string;
}

export interface FormaInput {
  readonly sessions: readonly LiftSession[];
  readonly clips: readonly ClipEntry[];
  /** What the movement measures; absent or `null` from a backend older than the field list. */
  readonly fields?: LiftFields;
  /** The line the trainer asked for; the movement's first offered line when it is not offered. */
  readonly line: LiftLine;
  readonly selection: TwoDatesSelection | null;
  readonly trend: boolean;
  readonly projection: boolean;
  readonly viewerUserId: string | null;
  /** Epoch milliseconds — the reference for "wczoraj, 19:42". */
  readonly now: number;
  /** The recording the trainer put in the panel from the chart; the newest when absent. */
  readonly dockClipId?: number | null;
}

export interface FormaRecordedPoint {
  readonly at: Date;
  readonly value: number;
  readonly clipId: number;
  readonly posterUrl: string | null;
  readonly dateLong: string;
  readonly valueLabel: string;
  readonly topSet: string | null;
}

export interface FormaChangeRow {
  readonly key: LiftLine;
  readonly label: string;
  readonly aValue: string | null;
  readonly bValue: string | null;
  readonly change: string | null;
  readonly changeIsGood: boolean;
  readonly percentLabel: string | null;
  readonly barPercent: number;
  readonly barSide: 'less' | 'more' | null;
}

export interface FormaSetCell {
  readonly setId: number;
  readonly label: string;
  readonly clipId: number | null;
}

export interface FormaSessionRow {
  readonly workoutExecutionId: number;
  readonly dateLabel: string;
  readonly cells: readonly FormaSetCell[];
  /** One per `FormaView.columns`. */
  readonly figures: readonly string[];
  readonly drop: string;
  readonly dropIsLoss: boolean;
  /** The session B stands on. */
  readonly current: boolean;
}

export type FormaDockChip =
  | { readonly kind: 'session'; readonly label: string }
  | { readonly kind: 'outside'; readonly label: string };

export interface FormaDockComment {
  readonly id: number;
  readonly person: ClipPersonView;
  readonly when: string;
  readonly body: string;
  /** „0:12,4", the moment the comment is pinned to, or `null` for a remark about the whole clip. */
  readonly stamp: string | null;
}

export interface FormaDock {
  readonly clipId: number;
  /** The recording itself, for the panel's small player. */
  readonly clip: ClipEntry;
  readonly kgReps: string | null;
  readonly chip: FormaDockChip;
  readonly dateLabel: string;
  readonly by: ClipPersonView;
  /** The newest page of the thread the timeline carries, newest first. */
  readonly comments: readonly FormaDockComment[];
  /** „15 wrz" when the panel shows an older recording than the newest, else `null`. */
  readonly chosenDate: string | null;
}

/** The folded panel: the newest recording's miniature and date. */
export interface FormaRail {
  readonly clipId: number;
  readonly posterUrl: string | null;
  readonly dateLabel: string;
  readonly unread: boolean;
}

export interface FormaLineOption {
  readonly key: LiftLine;
  readonly label: string;
  /** What the line measures, for the „?" beside the switch. */
  readonly meaning: string;
}

export interface FormaView {
  /** The movement has no counted set at all. */
  readonly empty: boolean;
  /** The movement has sessions, and none of them has a figure on the chosen line. */
  readonly noSeries: boolean;
  readonly headline: string;
  readonly current: { readonly label: string; readonly value: string; readonly dateLabel: string } | null;
  readonly sentence: TwoDatesSentence | null;
  readonly markers: readonly [TwoDatesMarker, TwoDatesMarker] | null;
  /**
   * The change table's heading, naming the two sessions by the dates their columns wear. Before A
   * and B stand it says „A i B", one line of the same type, so the head keeps its height.
   */
  readonly tableTitle: string;
  readonly readings: readonly MeasurementPoint[];
  readonly trend: readonly MeasurementPoint[] | null;
  readonly trendAvailable: boolean;
  readonly projection: TwoDatesProjection | null;
  readonly projectionAvailable: boolean;
  readonly xDomain: readonly [Date, Date] | null;
  readonly extraY: readonly number[];
  /** The line drawn, or `null` for a movement that measures nothing a line is drawn from. */
  readonly line: LiftLine | null;
  /** The lines offered for this movement, in the switch's order. */
  readonly lines: readonly FormaLineOption[];
  /**
   * What the fields promise before the sessions are read: the switch's options and choice through
   * the wait, and the change table's rows to reserve. No options while the fields are unknown.
   */
  readonly promised: {
    readonly lines: readonly FormaLineOption[];
    readonly line: LiftLine | null;
    readonly rows: number;
  };
  /** Which end of the chart's axis is better: pace is drawn faster-up. */
  readonly better: 'higher' | 'lower';
  readonly unit: string;
  /** How the chart prints a figure, where a plain number would be wrong: a clock or a pace. */
  readonly chartFigure: ((value: number) => string) | null;
  readonly lineLabel: string;
  readonly recorded: readonly FormaRecordedPoint[];
  readonly rows: readonly FormaChangeRow[];
  /** Said in the change table's reserve when it has no row to draw, else `null`. */
  readonly rowsNote: string | null;
  readonly tableCaption: string;
  readonly setColumns: number;
  /** The session table's figure columns, by label, before Spadek. */
  readonly columns: readonly string[];
  /** Whether the session table has a Spadek column: load with reps only. */
  readonly drop: boolean;
  readonly sessionRows: readonly FormaSessionRow[];
  readonly dock: FormaDock | null;
  readonly rail: FormaRail | null;
}

const times = (sessions: readonly LiftSession[]): readonly number[] =>
  sessions.map((session) => session.performedAt);

/** The pair a freshly opened movement starts on: its first and its latest session. */
export function defaultFormaSelection(sessions: readonly LiftSession[]): TwoDatesSelection | null {
  const all = times(sessions);
  const first = all[0];
  const last = all.at(-1);
  return first === undefined || last === undefined ? null : snapSelection(all, first, last);
}

/** Every gesture that moves A or B, snapped to a session date. */
export function moveFormaSelection(
  sessions: readonly LiftSession[],
  current: TwoDatesSelection | null,
  move: SelectionMove,
): TwoDatesSelection | null {
  if (move.kind === 'preset') return current;
  return moveOnTimes(times(sessions), current, move);
}

/**
 * What a press on a recorded point does, by its click count: a double click, or Enter on the
 * focused point (count 0), puts its recording in the panel. A single click does nothing, so A and B
 * never move under the first half of a double click; the plot beside the point still moves them.
 */
export function recordedPointShows(clickCount: number): boolean {
  return clickCount === 0 || clickCount === 2;
}

export function buildForma(input: FormaInput, { t, locale }: FormaDeps): FormaView {
  const { sessions } = input;
  const fields = input.fields ?? null;
  const reading = movementLines(fields, sessions, input.line);
  const { line } = reading;
  const percentNumber = numberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const dayMonth = dateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const dayMonthLong = dateTimeFormat(locale, { day: 'numeric', month: 'long' });
  const fullDate = dateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const words = { t, locale };

  // Each line's words are sized once, to the movement's largest figure on it.
  const sized = new Map<LiftLine, LineWords>();
  const wordsOf = (quantity: LiftLine): LineWords => {
    const known = sized.get(quantity);
    if (known !== undefined) return known;
    const made = sizedLineWords(sessions, quantity, words);
    sized.set(quantity, made);
    return made;
  };
  const drawn = line === null ? null : wordsOf(line);
  const lineOption = (key: LiftLine): FormaLineOption => ({
    key,
    label: t(`web:forma.line.${key}`),
    meaning: t(`web:forma.line.help.${key}`),
  });
  const person = (who: ClipEntry['author']): ClipPersonView =>
    clipPersonView(who, input.viewerUserId, t('web:forma.you'));

  const all = times(sessions);
  const selection =
    input.selection === null ? null : snapSelection(all, input.selection.a, input.selection.b);
  const sessionAt = (at: number): LiftSession | null =>
    sessions.find((session) => session.performedAt === at) ?? null;
  const figureAt = (session: LiftSession | null, quantity: LiftLine | null): number | null =>
    session === null || quantity === null ? null : sessionFigure(session, quantity);

  const readings: readonly Reading[] = drawn === null || line === null ? [] : lineReadings(sessions, line, drawn);
  const toPoints = (list: readonly Reading[]): readonly MeasurementPoint[] =>
    list.map((entry) => ({ x: new Date(entry.at), y: entry.value }));

  const { trendLine, trendAvailable, projected, projectionAvailable, xDomain } = chartAids(
    readings,
    all,
    input,
  );
  const first = all[0];

  const clipsNewestFirst = [...input.clips].sort(byNewestClip);
  const clipForSession = (workoutExecutionId: number): ClipEntry | null =>
    clipsNewestFirst.find((clip) => clip.anchor?.workoutExecutionId === workoutExecutionId) ?? null;
  const clipForSet = (setId: number): ClipEntry | null =>
    clipsNewestFirst.find((clip) => clip.anchor?.setId === setId) ?? null;

  const weighed = fields === null || fields.includes('weight');
  const topSetOf = (session: LiftSession): string | null => {
    if (!weighed) return null;
    const top = session.sets.find((entry) => entry.weightKg === session.topWeightKg) ?? null;
    const set = top === null ? null : setInFields(top, fields, words);
    return set === null ? null : t('web:forma.stage.topSet', { set });
  };

  const recorded: readonly FormaRecordedPoint[] =
    drawn === null
      ? []
      : sessions.flatMap((session) => {
          const clip = clipForSession(session.workoutExecutionId);
          const value = figureAt(session, line);
          if (clip === null || value === null) return [];
          return [
            {
              at: new Date(session.performedAt),
              value: drawn.toChart(value),
              clipId: clip.id,
              posterUrl: clip.media.posterUrl,
              dateLong: dayMonthLong.format(new Date(session.performedAt)),
              valueLabel: drawn.withUnit(value),
              topSet: topSetOf(session),
            },
          ];
        });

  const aSession = selection === null ? null : sessionAt(selection.a);
  const bSession = selection === null ? null : sessionAt(selection.b);
  // Rounded to what is printed, so a change too small to read is no change and is judged as none.
  const deltaOf = (quantity: LiftLine | null): number | null => {
    const a = figureAt(aSession, quantity);
    const b = figureAt(bSession, quantity);
    return a === null || b === null || quantity === null ? null : wordsOf(quantity).settle(b - a);
  };
  const sentence: TwoDatesSentence | null =
    selection === null || drawn === null
      ? null
      : sentenceOf(
          selection,
          deltaOf(line),
          { signed: drawn.signedWithUnit, isGood: drawn.isImprovement },
          { t, locale },
        );

  const markerOn = (key: MarkerKey, at: number): TwoDatesMarker => {
    const value = figureAt(sessionAt(at), line);
    return markerAt(key, at, all, value === null || drawn === null ? null : drawn.withUnit(value), {
      t,
      locale,
    });
  };

  const markers =
    selection === null ? null : ([markerOn('a', selection.a), markerOn('b', selection.b)] as const);

  const rows: readonly FormaChangeRow[] = reading.lines.map((quantity) => {
    const quantityWords = wordsOf(quantity);
    const a = figureAt(aSession, quantity);
    const b = figureAt(bSession, quantity);
    const delta = deltaOf(quantity);
    const percent = delta === null || a === null || a === 0 ? null : (delta / a) * 100;
    const label = t(`web:forma.rows.${quantity}`);
    return {
      key: quantity,
      label:
        quantityWords.rowUnit === null
          ? label
          : t('web:forma.rows.withUnit', { label, unit: quantityWords.rowUnit }),
      aValue: a === null ? null : quantityWords.figure(a),
      bValue: b === null ? null : quantityWords.figure(b),
      change: delta === null ? null : quantityWords.signedWithUnit(delta),
      changeIsGood: delta !== null && quantityWords.isImprovement(delta),
      percentLabel:
        percent === null || delta === 0
          ? null
          : signedText(`${percentNumber.format(Math.abs(percent))}%`, percent),
      ...divergingBar(percent, delta, FORMA_SCALE_PERCENT, FORMA_BAR_REACH),
    };
  });

  // The e1RM column keeps the canon's bare figure; every other column names its unit, which is what
  // tells metres from kilometres and a sum of seconds from a count.
  const columnFigure = (session: LiftSession, quantity: LiftLine): string => {
    const value = sessionFigure(session, quantity);
    if (value === null) return '·';
    const quantityWords = wordsOf(quantity);
    return quantity === 'e1rm' ? quantityWords.figure(value) : quantityWords.withUnit(value);
  };

  const sessionRows: readonly FormaSessionRow[] = [...sessions].reverse().map((session) => {
    const drop = reading.drop && line !== null ? sessionDrop(session, line) : null;
    return {
      workoutExecutionId: session.workoutExecutionId,
      dateLabel: dayMonth.format(new Date(session.performedAt)),
      cells: session.sets.map((entry) => ({
        setId: entry.setId,
        label: setInFields(entry, fields, words) ?? '·',
        clipId: clipForSet(entry.setId)?.id ?? null,
      })),
      figures: reading.columns.map((quantity) => columnFigure(session, quantity)),
      drop: drop === null || drawn === null ? '·' : signedText(drawn.figure(Math.abs(drop)), drop),
      dropIsLoss: drop !== null && drop < 0,
      current: selection !== null && session.performedAt === selection.b,
    };
  });

  const newest = newestClip(input.clips);
  const dockClip = input.clips.find((clip) => clip.id === input.dockClipId) ?? newest;
  const dock = ((): FormaDock | null => {
    if (dockClip === null) return null;
    const set = dockClip.anchor === null ? null : anchorWords(dockClip.anchor, sessions, words);
    const chip: FormaDockChip =
      set === null
        ? { kind: 'outside', label: t('web:forma.dock.outside') }
        : { kind: 'session', label: set.place };
    return {
      clipId: dockClip.id,
      clip: dockClip,
      kgReps: set?.load ?? null,
      chip,
      dateLabel: fullDate.format(new Date(dockClip.performedOn)),
      by: person(dockClip.author),
      comments: [...dockClip.comments].reverse().map((comment) => ({
        id: comment.id,
        person: person(comment.author),
        when: commentWhen(comment.createdAt, input.now, locale),
        body: comment.body,
        stamp: comment.offsetMs === null ? null : formatClock(comment.offsetMs / 1000, true, locale),
      })),
      chosenDate: dockClip === newest ? null : dayMonth.format(new Date(dockClip.performedOn)),
    };
  })();

  const recordedSessions = new Set(
    input.clips.flatMap((clip) => (clip.anchor === null ? [] : [clip.anchor.workoutExecutionId])),
  );
  const current = [...sessions].reverse().find((session) => figureAt(session, line) !== null);
  const currentValue = current === undefined ? null : figureAt(current, line);
  const headline =
    first === undefined
      ? t('web:forma.head.none')
      : t('web:forma.head.sessions', {
          count: sessions.length,
          date: dayMonthLong.format(new Date(first)),
          recorded: t('web:forma.head.recorded', {
            count: sessions.filter((session) => recordedSessions.has(session.workoutExecutionId)).length,
          }),
        });

  return {
    empty: sessions.length === 0,
    noSeries: sessions.length > 0 && readings.length === 0,
    headline:
      first !== undefined && line === 'e1rm'
        ? `${headline} ${t('web:forma.head.epley')}`
        : headline,
    current:
      current === undefined || currentValue === null || line === null || drawn === null
        ? null
        : {
            label: t(`web:forma.rows.${line}`),
            value: drawn.withUnit(currentValue),
            dateLabel: dayMonth.format(new Date(current.performedAt)),
          },
    sentence,
    markers,
    tableTitle:
      markers === null
        ? t('web:clientDetail.body.tableTitle')
        : t('web:forma.stage.tableTitle', { a: markers[0].dateLabel, b: markers[1].dateLabel }),
    readings: toPoints(readings),
    trend: trendLine === null ? null : toPoints(trendLine),
    trendAvailable,
    projection:
      projected === null || drawn === null
        ? null
        : {
            ...projected,
            label: t('web:clientDetail.body.projectionEnd', { value: drawn.chartFigure(projected.to.y) }),
          },
    projectionAvailable,
    xDomain,
    extraY: projected === null ? [] : [projected.to.y],
    line,
    lines: reading.lines.map(lineOption),
    promised: {
      lines: reading.promised.lines.map(lineOption),
      line: reading.promised.line,
      rows: reading.reserve,
    },
    better: drawn?.chartBetter ?? 'higher',
    unit: drawn?.unit ?? '',
    chartFigure: drawn?.clock === true ? drawn.chartFigure : null,
    lineLabel: line === null ? '' : t(`web:forma.line.${line}`),
    recorded,
    rows,
    rowsNote:
      rows.length > 0
        ? null
        : sessions.length === 0
          ? t('web:forma.stage.empty')
          : sessions.length < 2
            ? t('web:forma.stage.needsTwo')
            : t('web:forma.stage.noSeries'),
    tableCaption: t('web:clientDetail.body.tableCaption', { percent: FORMA_SCALE_PERCENT }),
    setColumns: sessions.reduce((widest, session) => Math.max(widest, session.sets.length), 0),
    columns: reading.columns.map((quantity) => t(`web:forma.line.${quantity}`)),
    drop: reading.drop,
    sessionRows,
    dock,
    rail:
      newest === null
        ? null
        : {
            clipId: newest.id,
            posterUrl: newest.media.posterUrl,
            dateLabel: dayMonth.format(new Date(newest.performedOn)),
            unread: newest.unread,
          },
  };
}
